import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import api from '../api/axios';
import { readTokenClaims, tokenCanManageUsers, accountAlwaysManagesUsers } from '../utils/sessionUser';

const passwordRule = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
const passwordHelp = 'At least 8 characters, with uppercase, lowercase, a number, and one of @$!%*?&';

const AddUserSchema = Yup.object().shape({
  email: Yup.string().email('Enter a valid email').required('Required'),
  password: Yup.string().matches(passwordRule, passwordHelp).required('Required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Required'),
  role: Yup.string().oneOf(['user', 'admin']).required('Required')
});

const UsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [allowed, setAllowed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [shownPasswords, setShownPasswords] = useState({});
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const loadUsers = async () => {
    const res = await api.get('/auth/users');
    setUsers(res.data);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const claims = readTokenClaims(token);
    const tokenAllows = tokenCanManageUsers(token);
    if (claims && claims.id) setCurrentUserId(claims.id);
    if (tokenAllows) setAllowed(true);

    let cancelled = false;
    (async () => {
      try {
        const me = await api.get('/auth/me');
        if (cancelled) return;
        setCurrentUserId(me.data.id);
        if (!me.data.canManageUsers && !tokenAllows) {
          navigate('/');
          return;
        }
        setAllowed(true);
        await loadUsers();
      } catch (err) {
        if (cancelled) return;
        if (tokenAllows) {
          try {
            await loadUsers();
          } catch (loadErr) {
            setError(loadErr.response?.data?.message || 'Could not load users');
          }
        } else if (err?.response?.status !== 401 && err?.response?.status !== 403) {
          setError(err.response?.data?.message || 'Could not load users');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const rememberPassword = (email, password) => {
    setShownPasswords((prev) => ({ ...prev, [email]: password }));
  };

  const grantAccess = async (user, canManageUsers) => {
    setError('');
    setMessage('');
    try {
      const res = await api.patch(`/auth/users/${user.id}`, { canManageUsers });
      setUsers((prev) => prev.map((row) => (row.id === user.id ? res.data : row)));
      setMessage(
        canManageUsers
          ? `${user.email} can now manage users.`
          : `${user.email} can no longer manage users from this switch.`
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update user');
    }
  };

  const removeUser = async (user) => {
    if (!window.confirm(`Remove ${user.email}? They will no longer be able to sign in.`)) return;
    setError('');
    setMessage('');
    try {
      await api.delete(`/auth/users/${user.id}`);
      setUsers((prev) => prev.filter((row) => row.id !== user.id));
      setMessage(`${user.email} was removed.`);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not remove user');
    }
  };

  const closePasswordDialog = () => {
    if (savingPassword) return;
    setPasswordTarget(null);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const savePassword = async () => {
    if (!passwordRule.test(newPassword)) {
      setPasswordError(passwordHelp);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords must match');
      return;
    }
    setSavingPassword(true);
    setPasswordError('');
    setError('');
    try {
      const res = await api.patch(`/auth/users/${passwordTarget.id}`, { password: newPassword });
      setUsers((prev) => prev.map((row) => (row.id === passwordTarget.id ? { ...row, ...res.data, password: undefined } : row)));
      rememberPassword(passwordTarget.email, newPassword);
      setMessage(`New password for ${passwordTarget.email}: ${newPassword}`);
      setPasswordTarget(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err.response?.data?.message || 'Could not set password');
    }
    setSavingPassword(false);
  };

  if (loading || !allowed) {
    return (
      <Container>
        <Box mt={4}>
          <Typography>Loading…</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box mt={4} mb={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4">Users</Typography>
        <Button variant="outlined" onClick={() => navigate('/')}>Back</Button>
      </Box>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Add user</Typography>
        <Formik
          initialValues={{ email: '', password: '', confirmPassword: '', role: 'user' }}
          validationSchema={AddUserSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            setError('');
            setMessage('');
            try {
              const res = await api.post('/auth/users', {
                email: values.email,
                password: values.password,
                role: values.role
              });
              rememberPassword(res.data.email, values.password);
              setMessage(`${res.data.email} can sign in with password ${values.password}.`);
              resetForm();
              await loadUsers();
            } catch (err) {
              setError(err.response?.data?.message || 'Could not add user');
            }
            setSubmitting(false);
          }}
        >
          {({ isSubmitting, errors, touched, values, handleChange }) => (
            <Form>
              <Field
                as={TextField}
                name="email"
                label="Email"
                fullWidth
                margin="normal"
                error={touched.email && !!errors.email}
                helperText={touched.email && errors.email}
              />
              <Field
                as={TextField}
                name="password"
                label="Password"
                type="password"
                fullWidth
                margin="normal"
                error={touched.password && !!errors.password}
                helperText={touched.password && errors.password}
              />
              <Field
                as={TextField}
                name="confirmPassword"
                label="Confirm password"
                type="password"
                fullWidth
                margin="normal"
                error={touched.confirmPassword && !!errors.confirmPassword}
                helperText={touched.confirmPassword && errors.confirmPassword}
              />
              <TextField
                select
                name="role"
                label="Role"
                value={values.role}
                onChange={handleChange}
                fullWidth
                margin="normal"
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </TextField>
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
              {message && <Alert severity="success" sx={{ mt: 2 }}>{message}</Alert>}
              <Button type="submit" variant="contained" disabled={isSubmitting} sx={{ mt: 2 }}>
                Add user
              </Button>
            </Form>
          )}
        </Formik>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Usernames are the email addresses. Existing passwords are stored as hashes and cannot be shown. A password appears here only after you set it.
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Can manage users</TableCell>
              <TableCell>Password</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => {
              const isSelf = Number(user.id) === Number(currentUserId);
              const alwaysAllowed = accountAlwaysManagesUsers(user);
              return (
                <TableRow key={user.id}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={!!user.canManageUsers || !!user.can_manage_users || alwaysAllowed}
                          disabled={alwaysAllowed}
                          onChange={(event) => grantAccess(user, event.target.checked)}
                        />
                      )}
                      label={alwaysAllowed ? 'Always allowed' : (user.can_manage_users ? 'Yes' : 'No')}
                    />
                  </TableCell>
                  <TableCell>
                    {shownPasswords[user.email] ? shownPasswords[user.email] : 'Hidden until you set a new one'}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Button size="small" variant="outlined" onClick={() => {
                        setPasswordTarget(user);
                        setNewPassword('');
                        setConfirmPassword('');
                        setPasswordError('');
                      }}>
                        Set password
                      </Button>
                      {!isSelf && (
                        <Button size="small" color="error" variant="outlined" onClick={() => removeUser(user)}>
                          Remove
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!passwordTarget} onClose={closePasswordDialog} fullWidth maxWidth="xs">
        <DialogTitle>Set password{passwordTarget ? ` for ${passwordTarget.email}` : ''}</DialogTitle>
        <DialogContent>
          <TextField
            label="New password"
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            fullWidth
            margin="normal"
            helperText={passwordHelp}
          />
          <TextField
            label="Confirm password"
            type="password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            fullWidth
            margin="normal"
          />
          {passwordError && <Alert severity="error" sx={{ mt: 1 }}>{passwordError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePasswordDialog} disabled={savingPassword}>Cancel</Button>
          <Button variant="contained" onClick={savePassword} disabled={savingPassword}>Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UsersPage;
